import React, { CSSProperties, ReactNode, useState } from 'react';
import { useSuccessAlert } from '../../../App.hooks';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { ContentVersion, RequestResolutionEnum, useCloseRequestMutation } from '../../../generated/graphql';

type FinalReviewRequestDialogProps = {
    onClose: () => void;
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    request: ContentVersion | undefined;
};

const FinalReviewRequestDialog = ({ visible, onClose, request, style }: FinalReviewRequestDialogProps) => {
    const [comment, setComment] = useState('');
    const [resolution, setResolution] = useState<RequestResolutionEnum>(RequestResolutionEnum.Published);
    const [error, setError] = useState<string | null>(null);
    const [closeRequest, { loading }] = useCloseRequestMutation();
    const setSuccessAlert = useSuccessAlert();

    const handleConfirm = () => {
        if (!comment.trim()) { setError('A comment is required.'); return; }
        if (!request?.id) { setError('The request could not be identified.'); return; }
        setError(null);
        closeRequest({ variables: { id: request.id, input: { resolution, message: comment } } })
            .then(() => { setSuccessAlert('Request Closed!'); setComment(''); onClose(); })
            .catch((err) => setError(err?.message || 'Unable to close the request.'));
    };

    return (
        <DialogBase
            title="Close Request"
            type="OkCancel"
            visible={visible}
            onClose={() => { setComment(''); setError(null); onClose(); }}
            onOkClick={handleConfirm}
            isLoading={loading}
            style={style}
        >
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Close <strong style={{ color: 'var(--text-primary)' }}>{request?.name}</strong> as published or declined.
            </p>
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Resolution</label>
                <select className="input-base mb-3" value={resolution} onChange={(e) => setResolution(e.target.value as RequestResolutionEnum)}>
                    <option value={RequestResolutionEnum.Published}>Publish content</option>
                    <option value={RequestResolutionEnum.Declined}>Decline request</option>
                </select>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Comment *</label>
                <textarea
                    className="input-base"
                    rows={3}
                    placeholder="Add a closure comment..."
                    value={comment}
                    onChange={(e) => { setComment(e.target.value); setError(null); }}
                />
                {error && <span className="text-xs" style={{ color: 'var(--error, #dc2626)' }}>{error}</span>}
            </div>
        </DialogBase>
    );
};

export default FinalReviewRequestDialog;
